"use client";

import { useState, useRef, useEffect } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import androidBadge from "../src/assets/googlebadge.svg";
import appleBadge from "../src/assets/appstorebadge.svg";
import vrheadset from "../src/assets/new_vr_image.webp";
import anamCaraImage from "../src/assets/AnamCaraImage.webp";
import appMockup from "../src/assets/AppMockupIndividual.webp";
import banner from "../src/assets/banner.webp";

import {
  Brain,
  CircleDollarSign,
  Activity,
  Headphones,
  ChartLine,
  Hospital,
  RectangleGoggles,
  HeartHandshake,
} from "lucide-react";

const testimonials = [
  {
    quote:
      "AnamVR helps me manage stress without needing to disappear into it. It gives you real tools to reset — not just a distraction, but actual techniques that carry into real life.",
    name: "Luca Miller, 20",
    title: "Philosophy Student",
  },
  {
    quote:
      "Most of my stress comes from being indoors and overwhelmed. AnamVR helps break that loop — the virtual nature spaces feel like a breath of fresh air.",
    name: "Ben O’Reilly, 22",
    title: "Engineering Student",
  },
  {
    quote:
      "I’m more of an ‘outdoors fixes everything’ type, but AnamVR surprised me. The VR environments actually made me feel like I’d stepped away from it all.",
    name: "Rory Doyle, 21",
    title: "Business Student",
  },
];

// Define the benefits array
const benefits = [
  {
    icon: <CircleDollarSign className="w-6 h-6" />,
    title: "Affordable, Expert-Backed Support",
    description:
      "Access clinically grounded tools and experiences without the high costs of traditional therapy.",
    color: "bg-[#7745b8]",
    textColor: "text-[#7745b8]",
    bgColor: "bg-[#7745b8]/10",
  },
  {
    icon: <Activity className="w-6 h-6" />,
    title: "Mindfulness & Meditation in VR",
    description:
      "Step into calming, immersive environments guided by experts to help you relax, refocus, and recharge.",
    color: "bg-[#487ce5]",
    textColor: "text-[#487ce5]",
    bgColor: "bg-[#487CE5]/10",
  },
  {
    icon: <Headphones className="w-6 h-6" />,
    title: "Tools to Break Unhelpful Thought Patterns",
    description:
      "Learn simple, effective ways to challenge negative thinking using proven psychological techniques based on CBT.",
    color: "bg-[#3c8c4f]",
    textColor: "text-[#3c8c4f]",
    bgColor: "bg-[#3C8C4F]/10",
  },
  {
    icon: <ChartLine className="w-6 h-6" />,
    title: "See Your Progress Clearly",
    description:
      "Track your mental health journey with simple, easy-to-understand dashboards — no jargon, just insight.",
    color: "bg-[#E74856]",
    textColor: "text-[#E74856]",
    bgColor: "bg-[#E74856]/10",
  },
  {
    icon: <Hospital className="w-6 h-6" />,
    title: "Self-Guided Clinical Assessments",
    description:
      "Use recognised tools like GAD-7 and PHQ-9 to check in with your mental wellbeing regularly — on your own terms.",
    color: "bg-[#F05984]",
    textColor: "text-[#F05984]",
    bgColor: "bg-[#F05984]/10",
  },
  {
    icon: <HeartHandshake className="w-6 h-6" />,
    title: "Personalised Recommendations",
    description:
      "Let AnamVR guide you to the most helpful content based on your mood, usage, and personal goals.",
    color: "bg-[#FF7A1A]",
    textColor: "text-[#FF7A1A]",
    bgColor: "bg-[#FF7A1A]/10",
  },
  {
    icon: <RectangleGoggles className="w-6 h-6" />,
    title: "Immersive VR Wellness — Without the Price Tag",
    description:
      "All you need is your smartphone and an affordable headset to unlock a whole new way to care for your mind.",
    color: "bg-[#D5B500]",
    textColor: "text-[#D5B500]",
    bgColor: "bg-[#D5B500]/10",
  },
];

export default function ForIndividuals() {
  const headerAnimation = useScrollAnimation();
  const introAnimation = useScrollAnimation({ threshold: 0.2 });
  const benefitsAnimation = useScrollAnimation({ threshold: 0.2 });
  const testimonialAnimation = useScrollAnimation({ threshold: 0.2 });
  const ctaAnimation = useScrollAnimation({ threshold: 0.2 });
  const imageAnimation = useScrollAnimation({ threshold: 0.2 });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [expandedBenefitIndex, setExpandedBenefitIndex] = useState<
    number | null
  >(0); // Start with first item expanded

  // Add this state to track successful form submission
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
    newsletter: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      newsletter: e.target.checked,
    }));
  };

  // Update the handleSubmit function to use Web3Forms API
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const formDataObj = new FormData();

    // Add your Web3Forms access key
    formDataObj.append("access_key", "e0d08ccb-aa34-4b38-9068-8531ab111d36");
    formDataObj.append(
      "subject",
      formData.subject || "New Message from AnamVR Website"
    );
    formDataObj.append("name", `${formData.firstName} ${formData.lastName}`);
    formDataObj.append("email", formData.email);
    formDataObj.append("message", formData.message);
    formDataObj.append("newsletter", formData.newsletter ? "Yes" : "No");

    // Show loading state if needed

    fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: formDataObj,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setFormSubmitted(true);
          // Reset form data
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            subject: "",
            message: "",
            newsletter: false,
          });
        } else {
          alert("Submission failed. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        alert("Something went wrong. Please try again.");
      });
  };

  const toggleBenefit = (index: number) => {
    // If clicking on item 0 and it's already expanded, do nothing to keep it open
    // Otherwise, toggle as normal
    if (index === 0 && expandedBenefitIndex === 0) {
      return;
    }

    setExpandedBenefitIndex(
      expandedBenefitIndex === index ? (index === 0 ? 0 : null) : index
    );
  };

  const goToNext = () => {
    if (activeTestimonial === testimonials.length - 1) {
      // If we're at the last testimonial, go to first without animation
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTestimonial(0);
        setIsTransitioning(false);
      }, 350); // Half the transition duration
    } else {
      setActiveTestimonial((prev) => prev + 1);
    }
  };

  const goToPrev = () => {
    if (activeTestimonial === 0) {
      // If we're at the first testimonial, go to last without animation
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTestimonial(testimonials.length - 1);
        setIsTransitioning(false);
      }, 350);
    } else {
      setActiveTestimonial((prev) => prev - 1);
    }
  };

  // Auto-slide testimonials every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      goToNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [activeTestimonial]);

  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Move this OUTSIDE of render/return:
  const benefitAnimations = [
    useScrollAnimation({ delay: 100 }),
    useScrollAnimation({ delay: 200 }),
    useScrollAnimation({ delay: 300 }),
    useScrollAnimation({ delay: 400 }),
    useScrollAnimation({ delay: 500 }),
    useScrollAnimation({ delay: 600 }),
    useScrollAnimation({ delay: 700 }),
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section
        ref={headerAnimation.ref}
        className={`w-full min-h-[710px] py-28 px-16 bg-gradient-to-br from-[#B7EDBB] to-[#BCDBEB] transition-opacity duration-700 ease-out ${
          headerAnimation.isVisible ? "animate-slide-up" : "opacity-0"
        }`}
      >
        <div className="max-w-7xl mx-auto md:px-6 lg:px-8">
          <div
            ref={headerAnimation.ref}
            className={`flex flex-col-reverse lg:flex-row items-center gap-8 lg:gap-16 transition-opacity duration-700 ease-out ${
              headerAnimation.isVisible ? "animate-slide-up" : "opacity-0"
            }`}
          >
            {/* Left Text Content */}
            <div className="lg:w-1/2 w-full">
              <div className="relative order-2 lg:order-1">
                <h1 className="text-4xl sm:text-5xl text-center lg:text-start font-normal text-[#2b2b2b] mb-8 leading-tight">
                  <b>Feel {""}</b> better. {""}
                  <b>Think {""}</b> clearer. {""}
                  <b>Breathe {""}</b> easier.
                </h1>
                <p className="text-lg md:text-xl text-center lg:text-start text-[#545454] mb-10 lg:pr-6">
                  AnamVR gives you expert-designed tools in the comfort of your
                  own home. Experience powerful support that helps you take
                  control of your mental wellbeing — anytime, anywhere.
                </p>
                <div className="flex justify-center items-center lg:justify-start lg:items-start flex-row gap-2">
                  <a
                    href="https://apps.apple.com/ie/app/anamvr/id6499339767"
                    target="_blank"
                    aria-label="Download on the App Store"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={appleBadge}
                      alt="Download on the App Store"
                      className="h-10 md:h-12 lg:h-14 w-auto"
                      width={135}
                      height={45}
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.anamvr&hl=en"
                    target="_blank"
                    aria-label="Get it on Google Play"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={androidBadge}
                      alt="Get it on Google Play"
                      className="h-10 md:h-12 lg:h-14 w-auto"
                      width={135}
                      height={45}
                    />
                  </a>
                </div>
              </div>
            </div>

            {/* Right Image Content */}
            <div className="lg:w-1/2 w-full flex mb-12 lg:mb-0 items-start justify-start mt-6 lg:mt-0 lg:items-center lg:justify-center">
              <div
                ref={imageAnimation.ref}
                className={`relative w-full max-w-[420px] h-fit lg:h-[600px] flex items-start justify-start lg:items-center lg:justify-center transition-opacity duration-700 ease-out ${
                  imageAnimation.isVisible ? "animate-slide-up" : "opacity-0"
                }`}
              >
                {/* Main phone mockup */}
                <img
                  src={appMockup || "/placeholder.svg"}
                  alt="AnamVR App Dashboard"
                  loading="lazy"
                  className="w-64 md:w-80 lg:w-96 h-auto rounded-3xl"
                  width={384}
                  height={768}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Get Section */}
      <section
        ref={benefitsAnimation.ref}
        className={`py-36 px-4 bg-gray-100 transition-opacity duration-700 ease-out ${
          benefitsAnimation.isVisible ? "animate-slide-up" : "opacity-0"
        }`}
      >
        <div className="max-w-7xl mx-auto flex flex-col-reverse lg:flex-row-reverse gap-32 items-center transition-opacity duration-700 ease-out">
          {/* Right Side - Image */}
          <div>
            <img
              src={anamCaraImage}
              alt="AnamVR Mockup"
              className="w-full"
              width={512}
              height={512}
            />
          </div>

          {/* Left Side - Benefits */}
          <div className="flex flex-col gap-1">
            <div className="text-start mb-4">
              <h2 className="text-2xl font-semibold text-[#2b2b2b] mb-2">
                Why AnamVR
              </h2>
            </div>

            {/* Accordion-style benefits */}
            {benefits.map((benefit, index) => {
              const animation = benefitAnimations[index];
              return (
                <div
                  key={index}
                  ref={animation.ref}
                  className={`border-b border-gray-100 last:border-0 transition-opacity duration-700 ease-out ${
                    animation.isVisible ? "animate-slide-up" : "opacity-0"
                  }`}
                >
                  <button
                    onClick={() => toggleBenefit(index)}
                    className={`w-full py-4 flex flex-col items-start justify-between rounded-lg px-3 transition-all ${
                      expandedBenefitIndex === index ? benefit.bgColor : ""
                    }`}
                  >
                    <div className="flex items-center w-full">
                      <span className="text-[#545454]/70 mr-4 text-sm font-mono w-7 text-right">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <div className="flex flex-row gap-4 text-start items-center flex-1">
                        <div
                          className={`${benefit.color} text-white p-2 rounded-lg flex items-center justify-center shrink-0 mt-1`}
                        >
                          {benefit.icon}
                        </div>
                        <span className={`font-semibold ${benefit.textColor}`}>
                          {benefit.title}
                        </span>
                      </div>

                      <div className="ml-auto flex items-center">
                        {expandedBenefitIndex === index ? (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={benefit.textColor}
                          >
                            <polyline points="18 15 12 9 6 15"></polyline>
                          </svg>
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-[#545454]"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Expandable content */}
                    <div
                      className={`overflow-hidden text-start mt-3 transition-all duration-300 ease-in-out ${
                        expandedBenefitIndex === index
                          ? "max-h-72 opacity-100 mb-6"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="px-12 flex items-start gap-4">
                        <p className="text-[#545454]">{benefit.description}</p>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Intro Section - Simplified Headspace-style */}
      <section
        ref={introAnimation.ref}
        className={`py-10 px-4 bg-gradient-to-r from-[#F5C2D7] to-[#FFD4C5] transition-opacity duration-700 ease-out ${
          introAnimation.isVisible ? "animate-slide-up" : "opacity-0"
        }`}
      >
        <div
          ref={introAnimation.ref}
          className={`max-w-6xl mx-auto transition-opacity duration-700 ease-out ${
            introAnimation.isVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="md:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2b2b2b] mb-6">
                Immersive mental wellness that fits your life
              </h2>
              <p className="text-xl text-[#2b2b2b] mb-10">
                Using your smartphone and a low-cost headset, experience
                powerful support that helps you take control of your mental
                wellbeing — anytime, anywhere.
              </p>
              <a
                href="https://shop.anamvr.com"
                target="_blank"
                aria-label="Get your headset from the AnamVR Shop"
                rel="noopener noreferrer"
                className="bg-[#7745b8] text-[#fafafa] text-xl px-6 py-4 rounded-full font-semibold hover:bg-[#6b3f9a] cursor-pointer transition-all duration-200 underline-offset-4 decoration-2"
              >
                Get your headset here
              </a>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <div className="relative">
                <img
                  src={vrheadset}
                  alt="VR headset with smartphone"
                  className="w-96 h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section - Clean, minimal style with centered navigation */}
      <section
        ref={testimonialAnimation.ref}
        className={`py-32 px-4 bg-white transition-opacity duration-700 ease-out ${
          testimonialAnimation.isVisible ? "animate-slide-up" : "opacity-0"
        }`}
      >
        <div
          ref={testimonialAnimation.ref}
          className={`max-w-5xl mx-auto transition-opacity duration-700 ease-out ${
            testimonialAnimation.isVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          {/* Testimonial content */}
          <div className="relative py-16">
            {/* Testimonial slider */}
            <div className="overflow-hidden relative w-full max-w-4xl mx-auto">
              <div
                className={`flex ${isTransitioning ? "" : "transition-transform duration-700 ease-in-out"}`}
                style={{
                  transform: `translateX(-${activeTestimonial * 100}%)`,
                }}
              >
                {testimonials.map((testimonial, idx) => (
                  <div key={idx} className="min-w-full px-4 py-6">
                    <div className="text-center">
                      <p className="text-3xl md:text-4xl font-medium text-[#545454] mb-12 leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                      <div className="mt-8">
                        <p className="text-xl font-medium text-[#7745b8]">
                          {testimonial.name}
                        </p>
                        <p className="text-[#545454]">{testimonial.title}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Simple centered navigation arrows */}
            <div className="flex justify-center gap-10 mt-16">
              <button
                onClick={goToPrev}
                className="transition-opacity opacity-100 hover:text-[#7745b8]"
                aria-label="Previous testimonial"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              <button
                onClick={goToNext}
                className="transition-opacity opacity-100 hover:text-[#7745b8]"
                aria-label="Next testimonial"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact-us"
        className="py-16 flex justify-center items-center"
      >
        <div className="max-w-7xl px-4 mx-auto w-full transition-opacity duration-700 ease-out">
          <div className="rounded-3xl overflow-hidden shadow-md">
            <div className="grid grid-cols-1 lg:grid-cols-12 bg-gradient-to-br from-[#eacfff]/30 via-[#bcdbeb]/30 to-[#e9ffdb]/30">
              {/* Left side - Contact info */}
              <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-between">
                <div>
                  <h2 className="text-4xl md:text-5xl font-bold text-[#7745b8] mb-6 text-center lg:text-left">
                    Contact us
                  </h2>

                  <div className="mb-8">
                    <p className="text-lg text-[#545454] mb-4 text-center lg:text-left">
                      Empowering you on your journey towards better mental
                      health.
                    </p>
                    <p className="text-[#545454] mb-6 text-center lg:text-left">
                      At AnamVR, our team is dedicated to revolutionising the
                      mental wellness space by offering accessible, affordable,
                      and convenient solutions for anxiety and mood-related
                      issues.
                    </p>
                    <p className="text-xl font-medium text-[#7745b8] text-center lg:text-left">
                      Do you have any questions for us? Fill out this form and
                      we will be in touch shortly.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right side - Contact form */}
              <div className="lg:col-span-7 bg-white p-8 md:p-12 rounded-tl-3xl rounded-bl-3xl">
                {!formSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="text-[#545454] mb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="firstName"
                            className="block text-sm mb-1"
                          >
                            First Name{" "}
                            <span className="text-[#7745b8]">(required)</span>
                          </label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7745b8]/50"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="lastName"
                            className="block text-sm mb-1"
                          >
                            Last Name{" "}
                            <span className="text-[#7745b8]">(required)</span>
                          </label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7745b8]/50"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="text-[#545454]">
                      <label htmlFor="email" className="block mb-1">
                        Email <span className="text-[#7745b8]">(required)</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7745b8]/50"
                      />
                    </div>

                    <div className="text-[#545454]">
                      <label htmlFor="subject" className="block mb-1">
                        Subject{" "}
                        <span className="text-[#7745b8]">(required)</span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7745b8]/50"
                      />
                    </div>

                    <div className="text-[#545454]">
                      <label htmlFor="message" className="block mb-1">
                        Message{" "}
                        <span className="text-[#7745b8]">(required)</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7745b8]/50"
                      ></textarea>
                    </div>

                    <div className="text-[#545454]">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="newsletter"
                          checked={formData.newsletter}
                          onChange={handleCheckboxChange}
                          className="rounded text-[#7745b8] focus:ring-[#7745b8]"
                        />
                        <span className="text-sm">
                          Subscribe to our newsletter for updates
                        </span>
                      </label>
                    </div>

                    <div className="flex justify-center lg:justify-start">
                      <button
                        type="submit"
                        className="bg-[#7745b8] hover:bg-[#7745b8]/90 text-white font-medium py-3 px-8 rounded-full flex items-center transition-colors"
                      >
                        Submit
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="p-6 rounded-xl bg-green-100 text-green-800 text-center">
                    <div className="flex flex-col items-center justify-center space-y-4 py-8">
                      <svg
                        className="w-16 h-16 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <h3 className="text-2xl font-bold">Thank you!</h3>
                      <p className="text-lg">
                        Your message has been submitted successfully.
                      </p>
                      <p>We'll get back to you as soon as possible.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Clean, modern style with app download focus */}
      <section
        ref={ctaAnimation.ref}
        className={`w-full bg-[#] pt-12 transition-opacity duration-700 ease-out ${
          ctaAnimation.isVisible ? "animate-slide-up" : "opacity-0"
        }`}
        style={{
          backgroundImage: `url(${banner})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <div
          ref={ctaAnimation.ref}
          className={`max-w-7xl mx-auto transition-opacity duration-700 ease-out ${
            ctaAnimation.isVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          <div className="flex flex-col-reverse lg:flex-row items-center justify-between lg:gap-12 px-4 lg:px-0">
            {/* Left side - Text content */}
            <div className="w-full">
              <div className="py-20 px-4 md:px-16">
                <h3 className="text-[#2b2b2b] text-2xl font-medium mb-3">
                  Download our app
                </h3>
                <h2 className="text-[#2b2b2b] text-4xl sm:text-6xl font-bold leading-tight mb-12">
                  The modern way
                  <br />
                  to manage your
                  <br />
                  mental wellness
                </h2>

                {/* App store badges */}
                <div className="flex flex-col md:flex-row gap-4">
                  <a
                    href="https://apps.apple.com/ie/app/anamvr/id6499339767"
                    target="_blank"
                    aria-label="Download on the App Store"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={appleBadge}
                      alt="Download on the App Store"
                      className="h-10 md:h-12 lg:h-14 w-auto"
                      width={135}
                      height={45}
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.anamvr&hl=en"
                    target="_blank"
                    aria-label="Get it on Google Play"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={androidBadge}
                      alt="Get it on Google Play"
                      className="h-10 md:h-12 lg:h-14 w-auto"
                      width={135}
                      height={45}
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
